// Coach-Governed Continuity Loop — persistence service for carry-forward review state.
// Hard boundaries: coach_only is always the default visibility.
// Never set leader-safe visibility automatically.
// Source type and source id must always come from the candidate.

import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  CarryForwardAction,
  CarryForwardCandidate,
  CarryForwardSourceTable,
} from "@/types/carryForward";

export type ContinuitySourceTable = CarryForwardSourceTable;

export type ContinuityReviewModeScope =
  | { mode: "leader"; leaderId: string }
  | { mode: "coach_practice"; leaderId?: null | undefined };

export interface ContinuityReview {
  id: string;
  coachId: string;
  leaderId: string | null;
  mode: "leader" | "coach_practice";
  sourceType: string;
  sourceId: string;
  sourceTable: ContinuitySourceTable | null;
  sourceRowId: string | null;
  sourceRecordItemIds: string[] | null;
  sourceInsightId: string | null;
  reviewState:
    | "carried_forward"
    | "reframed"
    | "retired"
    | "ignored_for_now"
    | "needs_review";
  originalTitle: string;
  reframedTitle: string | null;
  coachNote: string | null;
  visibility: "coach_only" | "coach_reviewed" | "not_shareable_yet";
  allowedUses: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  reviewedAt: string | null;
}

type ReviewState = ContinuityReview["reviewState"];

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isUuid(value: string | null | undefined): value is string {
  return typeof value === "string" && UUID_PATTERN.test(value);
}

function validateLeaderModeLeaderId(candidate: CarryForwardCandidate): Error | null {
  if (candidate.mode !== "leader") return null;
  if (isUuid(candidate.leaderId)) return null;
  return new Error(
    "leader-mode continuity reviews require a valid leader UUID before persistence",
  );
}

function mapActionToReviewState(action: CarryForwardAction): ReviewState {
  switch (action) {
    case "carry_forward":
      return "carried_forward";
    case "reframe":
      return "carried_forward";
    case "retire":
      return "retired";
    case "ignore_for_now":
      return "ignored_for_now";
    default: {
      const _exhaustive: never = action;
      console.error("[coachContinuityReviewService] Unknown action:", _exhaustive);
      return "needs_review";
    }
  }
}

function deriveSourceType(candidate: CarryForwardCandidate): string {
  // Prefer the first evidence fragment's source type when available.
  const firstFragment = candidate.evidenceFragments?.[0];
  if (firstFragment?.sourceType) {
    return firstFragment.sourceType;
  }
  return "coach_chat"; // sentinel for candidates without linked source fragments
}

function deriveSourceRecordId(candidate: CarryForwardCandidate): string {
  const firstSourceRecordItemId = candidate.sourceRecordItemIds.find((id) =>
    typeof id === "string" && id.trim().length > 0
  );
  return firstSourceRecordItemId ?? candidate.id;
}

function validateCandidateLineage(candidate: CarryForwardCandidate): Error | null {
  if (!candidate.sourceTable || !candidate.sourceRowId) {
    return new Error(
      "carry-forward candidates require sourceTable/sourceRowId lineage before persistence",
    );
  }
  if (candidate.sourceRecordItemIds.length > 1) {
    return new Error(
      "carry-forward candidates with multiple source rows require explicit bundle semantics before persistence",
    );
  }
  if (candidate.mode === "leader" && candidate.sourceTable !== "continuity_moments") {
    return new Error("leader carry-forward candidates must use continuity_moments lineage");
  }
  if (candidate.mode === "coach_practice" && candidate.sourceTable !== "coach_memory_nodes") {
    return new Error("coach-practice carry-forward candidates must use coach_memory_nodes lineage");
  }
  return null;
}

export function formatPostgrestTextInList(values: string[]): string {
  const encodedValues = values
    .filter((value) => value.trim().length > 0)
    .map((value) => `"${value.replace(/\\/g, "\\\\").replace(/"/g, "\\\"")}"`);
  return `(${encodedValues.join(",")})`;
}

/**
 * Save or update a coach review.
 * Upsert key: coach_id + source_id + source_type + leader_id.
 * Visibility defaults to "coach_only" — never set automatically to leader-safe.
 */
export async function upsertContinuityReview(params: {
  supabase: SupabaseClient;
  coachId: string;
  candidate: CarryForwardCandidate;
  action: CarryForwardAction;
  reframedTitle?: string;
  coachNote?: string;
}): Promise<{ error: Error | null }> {
  const { supabase, coachId, candidate, action, reframedTitle, coachNote } =
    params;

  const leaderValidationError = validateLeaderModeLeaderId(candidate);
  if (leaderValidationError) {
    return { error: leaderValidationError };
  }
  const lineageValidationError = validateCandidateLineage(candidate);
  if (lineageValidationError) {
    return { error: lineageValidationError };
  }

  const sourceType = deriveSourceType(candidate);
  const sourceId = deriveSourceRecordId(candidate);
  const reviewState = mapActionToReviewState(action);

  const payload = {
    coach_id: coachId,
    leader_id: candidate.mode === "coach_practice" ? null : candidate.leaderId,
    mode: candidate.mode,
    source_type: sourceType,
    source_id: sourceId,
    source_table: candidate.sourceTable,
    source_row_id: candidate.sourceRowId,
    source_record_item_ids: candidate.sourceRecordItemIds.length > 0
      ? candidate.sourceRecordItemIds
      : null,
    source_insight_id: candidate.sourceInsightId ?? null,
    review_state: reviewState,
    original_title: candidate.title,
    reframed_title: reframedTitle ?? null,
    coach_note: coachNote ?? null,
    // visibility always defaults to coach_only — never set leader-safe automatically
    visibility: "coach_only" as const,
    allowed_uses: {},
    reviewed_at: new Date().toISOString(),
  };

  const { error } = await (
    supabase
      .from("coach_continuity_reviews")
      .upsert(payload, {
        onConflict: "coach_id,source_id,source_type,leader_id",
      }) as Promise<{ error: unknown }>
  );

  if (error) {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }
  return { error: null };
}

function mapRowToContinuityReview(row: Record<string, unknown>): ContinuityReview {
  return {
    id: row.id as string,
    coachId: row.coach_id as string,
    leaderId: (row.leader_id as string | null) ?? null,
    mode: row.mode as "leader" | "coach_practice",
    sourceType: row.source_type as string,
    sourceId: row.source_id as string,
    sourceTable: (row.source_table as ContinuitySourceTable | null) ?? null,
    sourceRowId: (row.source_row_id as string | null) ?? null,
    sourceRecordItemIds: (row.source_record_item_ids as string[] | null) ?? null,
    sourceInsightId: (row.source_insight_id as string | null) ?? null,
    reviewState: row.review_state as ContinuityReview["reviewState"],
    originalTitle: row.original_title as string,
    reframedTitle: (row.reframed_title as string | null) ?? null,
    coachNote: (row.coach_note as string | null) ?? null,
    visibility: row.visibility as ContinuityReview["visibility"],
    allowedUses: (row.allowed_uses as Record<string, unknown>) ?? {},
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
    reviewedAt: (row.reviewed_at as string | null) ?? null,
  };
}

/**
 * Fetch reviews for a coach, filtered by mode and optionally by leader.
 *
 * `mode` is required by design: coach_practice and leader reviews must never
 * mix in one result set. buildContinuityTensions is mode-agnostic, so the
 * governance contract is that every fetch pre-filters by mode — an unfiltered
 * fetch leaks coach-practice content into leader-context surfaces.
 * Returns camelCase ContinuityReview objects mapped from DB snake_case columns.
 */
export async function fetchContinuityReviews(params: {
  supabase: SupabaseClient;
  coachId: string;
  reviewState?: "carried_forward" | "reframed" | "retired" | "ignored_for_now" | "needs_review";
} & ContinuityReviewModeScope): Promise<{ data: ContinuityReview[] | null; error: Error | null }> {
  const { supabase, coachId, leaderId, mode, reviewState } = params;

  if (mode === "leader" && !isUuid(leaderId)) {
    return {
      data: null,
      error: new Error("leader continuity review fetches require a valid leader UUID"),
    };
  }

  let query = supabase
    .from("coach_continuity_reviews")
    .select("*")
    .eq("coach_id", coachId)
    .eq("mode", mode);

  if (mode === "coach_practice") {
    query = query.is("leader_id", null);
  } else if (leaderId !== undefined) {
    query = query.eq("leader_id", leaderId);
  }

  if (reviewState !== undefined) {
    query = query.eq("review_state", reviewState);
  }

  const { data, error } = await (query as Promise<{
    data: Record<string, unknown>[] | null;
    error: unknown;
  }>);

  if (error) {
    return {
      data: null,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }

  const reviews = (data ?? []).map(mapRowToContinuityReview);
  return { data: reviews, error: null };
}

export async function fetchSuppressedContinuitySourceRowIds(params: {
  supabase: SupabaseClient;
  coachId: string;
  sourceTable: ContinuitySourceTable;
} & ContinuityReviewModeScope): Promise<{ data: string[] | null; error: Error | null }> {
  const { supabase, coachId, mode, leaderId, sourceTable } = params;

  if (mode === "leader" && !isUuid(leaderId)) {
    return {
      data: null,
      error: new Error("leader continuity suppression fetches require a valid leader UUID"),
    };
  }

  let query = supabase
    .from("coach_continuity_reviews")
    .select("source_row_id")
    .eq("coach_id", coachId)
    .eq("mode", mode)
    .eq("source_table", sourceTable)
    .in("review_state", ["retired", "ignored_for_now", "carried_forward", "reframed"]);

  if (mode === "coach_practice") {
    query = query.is("leader_id", null);
  } else {
    query = query.eq("leader_id", leaderId);
  }

  const { data, error } = await (query as Promise<{
    data: Array<{ source_row_id?: string | null }> | null;
    error: unknown;
  }>);

  if (error) {
    return {
      data: null,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }

  return {
    data: (data ?? [])
      .map((row) => row.source_row_id)
      .filter((sourceRowId): sourceRowId is string =>
        typeof sourceRowId === "string" && sourceRowId.trim().length > 0
      ),
    error: null,
  };
}
